import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useMerchantPermissions } from '../../contexts/MerchantPermissionContext';
import AccessDenied from '../Error/AccessDenied';
import { Box, CircularProgress, Typography } from '@mui/material';

/**
 * Route Permission Guard component
 * Protects routes by checking if user has specific module and action permissions
 */
const RoutePermissionGuard = ({ 
  children, 
  moduleName, 
  actionName = 'view',
  redirectTo = '/access-denied',
  showAccessDenied = false  // Changed default to false to use redirect instead
}) => {
  const { hasPermission, loading, initialized, error } = useMerchantPermissions();
  const location = useLocation();
  
  // Check if user has the required permission (memoized to prevent unnecessary recalculations)
  // Move this before any early returns to comply with React hooks rules
  const hasRequiredPermission = useMemo(() => {
    // Don't check permissions until we're fully initialized to prevent false negatives
    if (!initialized) return true;
    return hasPermission(moduleName, actionName);
  }, [hasPermission, moduleName, actionName, initialized]);
  
  // Show loading while permissions are being fetched or not initialized yet
  // Add a small delay to prevent flickering during page refreshes
  if (loading || !initialized) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="50vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // Handle permission loading error
  if (error) {
    console.error('Permission check error:', error);
    if (showAccessDenied) {
      return <AccessDenied />;
    }
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (!hasRequiredPermission) {
    
    // Prevent redirect loop by checking if we're already at the redirect destination
    if (location.pathname === redirectTo) {
      return <AccessDenied moduleName={moduleName} actionName={actionName} />;
    }
    
    if (showAccessDenied) {
      return <AccessDenied moduleName={moduleName} actionName={actionName} />;
    }
    
    // Redirect to access denied page with state information
    return <Navigate 
      to={redirectTo} 
      state={{ 
        from: location,
        moduleName: moduleName,
        actionName: actionName,
        deniedPath: location.pathname
      }} 
      replace 
    />;
  }

  // User has permission, render the protected component
  return children;
};

export default RoutePermissionGuard; 